const DECIMAL_TEXT = /^([+-]?)(?:(\d+)(?:\.(\d*))?|\.(\d+))(?:[eE]([+-]?\d+))?$/u;

function shiftDecimalText(value: string, places: number): string {
  if (value.length > 1000) throw new RangeError('Decimal text is too long.');
  const match = DECIMAL_TEXT.exec(value);
  if (match == null) throw new TypeError('Value must be a decimal number.');

  const sign = match[1] === '-' ? '-' : '';
  const integer = match[2] ?? '0';
  const fraction = match[3] ?? match[4] ?? '';
  const exponent = Number(match[5] ?? '0');
  if (!Number.isSafeInteger(exponent) || Math.abs(exponent) > 10_000) {
    throw new RangeError('Decimal exponent is outside the supported range.');
  }
  let digits = integer + fraction;
  const leadingZeros = digits.length - digits.replace(/^0+/u, '').length;
  digits = digits.slice(leadingZeros);

  if (digits.length === 0) return '0';

  const decimalPosition = integer.length + exponent + places - leadingZeros;
  const shifted = decimalPosition <= 0
    ? `0.${'0'.repeat(-decimalPosition)}${digits}`
    : decimalPosition >= digits.length
      ? `${digits}${'0'.repeat(decimalPosition - digits.length)}`
      : `${digits.slice(0, decimalPosition)}.${digits.slice(decimalPosition)}`;

  return `${sign}${shifted}`;
}

/** Convert a stored ratio to editable percentage text without floating-point multiplication artifacts. */
export function ratioToPercentText(value: number): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new RangeError('Ratio must be a finite number.');
  }
  if (value === 0) return '0';

  const shifted = shiftDecimalText(value.toString(), 2);
  return shifted;
}

/** Convert editable percentage text to its persisted ratio without changing ordinary ratio fields. */
export function percentTextToRatio(value: string): number {
  if (typeof value !== 'string' || !DECIMAL_TEXT.test(value)) {
    throw new TypeError('Percentage must be a decimal number.');
  }
  if (value.length > 1000) throw new RangeError('Decimal text is too long.');
  const match = DECIMAL_TEXT.exec(value)!;
  const exponent = Number(match[5] ?? '0');
  if (!Number.isSafeInteger(exponent) || Math.abs(exponent) > 10_000) {
    throw new RangeError('Decimal exponent is outside the supported range.');
  }
  if (!/[1-9]/u.test(`${match[2] ?? '0'}${match[3] ?? match[4] ?? ''}`)) return 0;

  const shifted = shiftDecimalText(value, -2);
  const ratio = Number(shifted);
  if (!Number.isFinite(ratio) || ratio === 0) {
    throw new RangeError('Ratio is outside the supported finite number range.');
  }
  return ratio;
}
